import {
  createServiceType,
  ServiceContainer,
} from '@tv2-player/service-container/src/serviceContainer';
import { OverseerTrackPluginCallService } from './overseer';
import type { OverseerOptions } from './overseerOptions';
import { OverseerMessageHandler } from './overseerMessageHandler';
import { devFlags } from '../devFlags';

export const OverseerServiceType = createServiceType<OverseerService>();

export class OverseerService implements OverseerTrackPluginCallService {
  #enabled: boolean = false;
  #sessionFn: () => string;
  #messageHandlerFn;

  constructor(options: OverseerOptions = {}) {
    this.#sessionFn = options.sessionFn ?? staticSessionIdFn;
    this.#messageHandlerFn =
      options.messageHandlersFn ?? (() => ServiceContainer.get(OverseerMessageHandler));

    if (ServiceContainer.config.get(devFlags.enableOverseer)) {
      this.enable();
    }
  }

  static register(options: OverseerOptions = {}) {
    // We register both our self and a OverseerTrackPluginCallService
    // - as the worker has a special implementation of OverseerTrackPluginCallService on Snowpiercer
    ServiceContainer.addInstance(new OverseerService(options))
      .as(OverseerServiceType)
      .as(OverseerTrackPluginCallService);
  }

  get enabled() {
    return this.#enabled;
  }

  enable() {
    this.#enabled = true;
  }

  disable() {
    this.#enabled = false;
  }

  /**
   * Track a plugin call, and be able to see it in the statistics overlay
   * @param plugin The name of the plugin / group that the call should be shown in
   * @param name The name / label of the call to show in the UI
   * @param value The value of the call, if any
   */
  trackPluginCall = (plugin: string, name: string, value?: any, metadata?: any) => {
    try {
      if (!this.#enabled) {
        return;
      }

      // We create the basic data structure if needed, and then add 1 to the number of calls

      // Uncomment to get all overseer calls in the console
      // const logger = createDebugLogger(plugin);
      // logger.log(name, value);

      const messageHandlers = this.#messageHandlerFn();

      messageHandlers.forEach(handler => {
        handler.createSession(this.#sessionFn());
        handler.trackPluginCall(plugin, name, value, metadata);
      });
    } catch (e) {
      // trackPluginCall should never throw an error, so we just console.log it and move on

      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  setMetadata(metadata: Record<string, any>): void {
    try {
      if (!this.#enabled) {
        return;
      }

      const messageHandlers = this.#messageHandlerFn();

      messageHandlers.forEach(handler => {
        handler.createSession(this.#sessionFn());
        handler.setMetadata(metadata);
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }
}

const sessionId = new Date().getTime().toString();

export const staticSessionIdFn = () => {
  return sessionId;
};

export const sessionIdFnFromStorage = () => {
  let sessId = sessionStorage.getItem('overseerSessionId');

  if (!sessId) {
    sessId = sessionId;
    sessionStorage.setItem('overseerSessionId', sessId);
  }

  return sessId;
};
